/*
 * "New channel" (spec 05 §2.1, goal :36). A member may open a channel — the matrix says so and has
 * said so since the spine was built; until this dialog existed the only way to use that permission
 * was a fetch typed into the browser's console (S0-REPORT §2).
 *
 * Two decisions are worth knowing. The short name is derived from the name while nobody has touched it,
 * and left alone afterwards: it is the address, so it has to be editable, and asking for it before
 * offering one makes every channel cost two decisions instead of one. It is also written with its hash
 * inside the field, because that is what the address will look like everywhere else.
 *
 * The other decision is who can find the channel, as three cards: *The team* (chosen), *Chosen
 * groups* and *Private*. A channel belongs to groups and everybody in one of them finds it and may
 * join it; *The team* sends no group at all, because a channel created without owners belongs to the
 * team members group on the server, and *Chosen groups* opens the groups checklist right here under
 * the cards — never a second dialog over this one. *Private* is a conversation of the people added to
 * it, and has no groups. Each card is its word and, under it, the sentence of what choosing it means.
 *
 * A guest never gets here: the doors to this dialog are drawn for team members only.
 */

import { useId, useMemo, useState } from "react"
import { GroupChecklist } from "../../../core/groups/GroupChecklist.tsx"
import { t as coreT } from "../../../core/i18n/index.ts"
import type { GroupId } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Button, Dialog, Field, FormAlert } from "../../../ui/index.ts"
import { createConversation, type CreateChannelInput } from "../conversation/api.ts"
import { goTo, whenConversationArrives } from "../conversation/opening.ts"
import { ownerCandidates } from "../conversation/owners.ts"
import { t, type ChatKey } from "../i18n/t.ts"
import { conversationPath } from "../navigation.ts"
import "./sidebar.css"
import { isValidSlug, SLUG_MAX, slugify } from "./slug.ts"

/** Who can find the channel: the team, the groups ticked below the cards, or only those added. */
type FindBy = "team" | "groups" | "private"

const FIND_BY = [
	{ value: "team", label: "chat.newChannel.team.label", explain: "chat.newChannel.team.explain" },
	{
		value: "groups",
		label: "chat.newChannel.groups.label",
		explain: "chat.newChannel.groups.explain",
	},
	{
		value: "private",
		label: "chat.newChannel.private.label",
		explain: "chat.newChannel.private.explain",
	},
] as const satisfies { value: FindBy; label: ChatKey; explain: ChatKey }[]

/** What the create route is sent for each answer; the name, the address and the topic are the form's. */
function placeOf(
	findBy: FindBy,
	groups: GroupId[],
): Pick<CreateChannelInput, "kind" | "ownerGroupIds"> {
	switch (findBy) {
		case "team":
			return { kind: "channel" }
		case "groups":
			return { kind: "channel", ownerGroupIds: groups }
		case "private":
			return { kind: "private" }
	}
}

export function NewChannelDialog({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const [name, setName] = useState("")
	const [slug, setSlug] = useState("")
	/** Set the moment somebody edits the short name; the derivation stops for good after that. */
	const [slugEdited, setSlugEdited] = useState(false)
	const [findBy, setFindBy] = useState<FindBy>("team")
	const [chosen, setChosen] = useState<GroupId[]>([])
	const [topic, setTopic] = useState("")
	const radioName = useId()
	const groups = useModel((model) => model.groups)
	const candidates = useMemo(() => ownerCandidates(groups), [groups])

	const effectiveSlug = slugEdited ? slug : slugify(name)
	const create = useAsyncAction(async () => {
		const created = await createConversation({
			...placeOf(findBy, chosen),
			slug: effectiveSlug,
			name: name.trim(),
			topic: topic.trim(),
			memberIds: [],
		})
		await whenConversationArrives(created.conversation.id)
		onOpenChange(false)
		reset()
		goTo(conversationPath(created.conversation.slug ?? effectiveSlug))
	})

	const canSubmit =
		name.trim() !== "" &&
		isValidSlug(effectiveSlug) &&
		(findBy !== "groups" || chosen.length > 0) &&
		!create.busy

	function reset() {
		setName("")
		setSlug("")
		setSlugEdited(false)
		setFindBy("team")
		setChosen([])
		setTopic("")
		create.reset()
	}

	function submit() {
		if (canSubmit) void create.run()
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				onOpenChange(next)
				if (!next) reset()
			}}
			title={t("chat.newChannel.title")}
			description={t("chat.newChannel.body")}
			footer={
				<div className="tb-dialog__actions">
					<Button
						variant="neutral"
						label={coreT("action.cancel")}
						onClick={() => onOpenChange(false)}
					/>
					<Button
						variant="primary"
						label={t("chat.newChannel.create")}
						disabled={!canSubmit}
						onClick={submit}
					/>
				</div>
			}
		>
			<form
				className="tb-fields"
				onSubmit={(event) => {
					event.preventDefault()
					submit()
				}}
			>
				<Field label={t("chat.newChannel.name")}>
					{(props) => (
						<input
							{...props}
							className="tb-field__control"
							value={name}
							maxLength={80}
							onChange={(event) => setName(event.target.value)}
						/>
					)}
				</Field>
				{/* The short name is an address, and an address starts with a hash: the `#` stands inside
				    the field rather than in a hint under it, so what is typed reads as what it becomes. */}
				<Field
					label={t("chat.newChannel.slug")}
					hint={t("chat.newChannel.slugHint")}
					{...(effectiveSlug !== "" && !isValidSlug(effectiveSlug)
						? { error: t("chat.newChannel.slugInvalid") }
						: {})}
				>
					{({ className, ...props }) => (
						<span className={`${className} tb-field__control--prefixed`}>
							<span className="tb-field__prefix" aria-hidden="true">
								{"#"}
							</span>
							<input
								{...props}
								value={effectiveSlug}
								maxLength={SLUG_MAX}
								onChange={(event) => {
									setSlugEdited(true)
									setSlug(event.target.value)
								}}
							/>
						</span>
					)}
				</Field>
				{/* Who can find it, as three cards: a short closed set is cards and never a dropdown (§6.3).
				    Native radios, one tab stop; a card is named by its word and described by its sentence. */}
				<fieldset className="tb-choices">
					<legend className="tb-choices__legend">{t("chat.newChannel.findBy")}</legend>
					{FIND_BY.map((option) => (
						<label className="tb-choice tb-choice--explained" key={option.value}>
							<input
								type="radio"
								name={radioName}
								checked={findBy === option.value}
								aria-labelledby={`${radioName}-${option.value}`}
								aria-describedby={`${radioName}-${option.value}-explain`}
								onChange={() => setFindBy(option.value)}
							/>
							<span className="tb-choice__body">
								<span className="tb-choice__label" id={`${radioName}-${option.value}`}>
									{t(option.label)}
								</span>
								<span className="tb-choice__explain" id={`${radioName}-${option.value}-explain`}>
									{t(option.explain)}
								</span>
							</span>
						</label>
					))}
				</fieldset>
				{findBy === "groups" ? (
					<GroupChecklist
						candidates={candidates}
						value={chosen}
						onChange={setChosen}
						legend={t("chat.newChannel.groups.legend")}
					/>
				) : null}
				<Field label={t("chat.newChannel.topic")} hint={t("chat.newChannel.topicHint")}>
					{(props) => (
						<input
							{...props}
							className="tb-field__control"
							value={topic}
							maxLength={250}
							onChange={(event) => setTopic(event.target.value)}
						/>
					)}
				</Field>
				<FormAlert messageKey={create.error} />
			</form>
		</Dialog>
	)
}
